import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';
import useInterval from "use-interval";
import {remainingSessionLifetime} from './remainingSessionLifetimeCheck';
import useStarfishAPI from "../hooks/useStarfishAPI";
import ConfirmDialog from "common/ConfirmDialog";
import classnames from "classnames";
import confirmCss from "common/ConfirmDialog.scss";
import css from "./SessionTimeoutWarning.scss";

// When a logged in user's session is about to timeout, display the warning to the user with an option to stay logged in,
// if possible.
const SessionTimeoutWarning = ({hitApiAfterPageLoadInSecs}) => {
    const [isTimeoutModalOpen, setTimeoutModalOpen] = useState(false);

    // Interval at which the api is checked for user's session lifetime state.
    // This is initially set to 9 minutes after the page is loaded, and then dynamically calculated based on the
    // api response of user inactivity time, tenant's session timeout duration and the maximum possible duration of the session.
    const [delayToNextSessionLifeCheckMilliSecs, setDelayToNextSessionLifeCheck] = useState(hitApiAfterPageLoadInSecs * 1000);

    // Represents whether user's session is extendable when the session is about to expire.
    // When the session is near its maximum possible length, it returns false.
    const [isSessionLifeExtendable, setSessionLifeExtendable] = useState(true);
    const [sessionLifeTimeResponse, sessionLifeTimeAPI] = useStarfishAPI({isNewAPI: true});
    const [, updateActivityAPI ] = useStarfishAPI();

    const updateTimeoutWarningState = ({idleSecondsUntilSessionExpiry, maxSessionLifetimeLeftSecs}) => {
        const sessionLifetimeState = remainingSessionLifetime(idleSecondsUntilSessionExpiry, maxSessionLifetimeLeftSecs);
        setDelayToNextSessionLifeCheck(sessionLifetimeState.checkSessionExpiryAfterSecs * 1000);
        setSessionLifeExtendable(sessionLifetimeState.isSessionLifeExtendable);
        setTimeoutModalOpen(sessionLifetimeState.isSessionExpiring);
    }

    // Call the api periodically to check on user's session lifetime state.
    // The api MUST be appended with '?sessionKeepaliveSuppressed=true' to not make this api call count as an user activity by itself.
    useInterval(() => {
        const URL = `../api/session-lifetime.json?sessionKeepaliveSuppressed=true`;
        sessionLifeTimeAPI(URL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        })
    }, delayToNextSessionLifeCheckMilliSecs);

    useEffect(() => {
        if (!sessionLifeTimeResponse.success) return;
        updateTimeoutWarningState(sessionLifeTimeResponse.data);
    }, [sessionLifeTimeResponse]);

    const extendSessionLife = () => updateActivityAPI(`../api/update-activity.html`, { method: 'POST' });

    return <ConfirmDialog isOpen={isTimeoutModalOpen}
                          focusTrap={true}
                          headerText={isSessionLifeExtendable ? "Session is about to expire. Stay logged in?" : "We're logging you out soon."}
                          descriptionText={isSessionLifeExtendable ?
                              "For security purposes, we will log you out soon unless you choose to stay logged in below."
                              : "Your session has expired and you will be logged out soon. Please save any work. Afterward, you will need to go to your page to get back in."}
                          confirmText={isSessionLifeExtendable ? 'Stay logged in' : 'Ok'}
                          overlayClassName={classnames(confirmCss.overlay, css.overlay)}
                          confirmButtonClassName={css.confirmButton}
                          onConfirm={async () => {
                              isSessionLifeExtendable && await extendSessionLife();
                              setTimeoutModalOpen(false)
                          }} />
}

SessionTimeoutWarning.defaultProps = {
    hitApiAfterPageLoadInSecs: 540 // 9 minutes
}

SessionTimeoutWarning.propTypes = {
    hitApiAfterPageLoadInSecs: PropTypes.number
}

export default SessionTimeoutWarning;
